import type { HighlightBox } from './highlighter'
import type { DocumentPage, Statement } from './types'

// The side inset matches the map's gutter, so the pages line up with everything above them.
export const STRIP_INSET = 16
export const STRIP_TOP = 8
export const BAND_GAP = 16

// These match fixed heights in the band and card markup. If that markup changes, change these
// with it: a wrong value leaves dead space under the pages or cuts their bottoms off.
const BAND_BORDER = 1
const BAND_PADDING = 12
const CARD_GAP = 4
// The rule drawn between two pages that are not next to each other in the file. Wide enough to
// read against a page's own edge, which is the only other line in a band.
const JUMP_RULE = 3
const TITLE_ROW = 20
const SOURCE_ROW = 24
const CARD_PADDING = 4
const CARD_CAPTION = 20
export const PAGE_ASPECT = 11 / 8.5
export const SHEET_ASPECT_RATIO = '8.5 / 11'

export interface StripMetrics {
  cardWidth: number
  cardHeight: number
  bandHeight: number
  rowHeight: number
  pageTop: number
  // From one page card to the next, gap included.
  pageStride: number
}

// Every size comes from the card width and is never measured, so a band's offset is known before
// it is on screen. That is what lets the strip virtualize and the scrubber scale the file down.
export const stripMetrics = (cardWidth: number): StripMetrics => {
  const artHeight = Math.round((cardWidth - CARD_PADDING * 2) * PAGE_ASPECT)
  const cardHeight = artHeight + CARD_PADDING * 2 + CARD_CAPTION
  const bandHeight =
    (BAND_BORDER + BAND_PADDING) * 2 + TITLE_ROW + SOURCE_ROW + cardHeight
  return {
    cardWidth,
    cardHeight,
    bandHeight,
    rowHeight: bandHeight + STRIP_TOP * 2,
    pageTop: STRIP_TOP + BAND_BORDER + BAND_PADDING + TITLE_ROW + SOURCE_ROW,
    pageStride: cardWidth + CARD_GAP,
  }
}

export interface StripPageSlot {
  page: DocumentPage
  left: number
  // The page before this one in the band is not the page before it in the file.
  afterAJump: boolean
}

export interface StripBand {
  statementId: string
  label: string
  left: number
  width: number
  pages: StripPageSlot[]
}

export interface StripLayout {
  bands: StripBand[]
  totalWidth: number
  metrics: StripMetrics
}

// All the strip reads of a statement: which statement it is, and the pages it holds in file
// order. The pages themselves, not their numbers, because a band draws them and says which
// uploads they were scanned in.
export interface BandSource {
  statement: Statement
  pages: DocumentPage[]
}

export type BandLabel = (source: BandSource) => string

interface PageRow {
  slots: StripPageSlot[]
  width: number
}

// Two pages read on from each other only when they are the next sheet of the same upload. The
// project numbers its uploads end to end, so the seam between two of them is consecutive
// numbers and still a jump.
const followsOnFrom = (page: DocumentPage, previous: DocumentPage) =>
  page.source.fileId === previous.source.fileId &&
  page.number === previous.number + 1

// A statement's pages in file order, left to right, with room for a rule wherever they jump.
const pageRow = (
  pages: DocumentPage[],
  rowLeft: number,
  cardWidth: number,
): PageRow => {
  let at = rowLeft
  const slots = pages.map((page, index) => {
    const previous = pages[index - 1]
    const afterAJump = previous !== undefined && !followsOnFrom(page, previous)
    if (index > 0) at += CARD_GAP + (afterAJump ? JUMP_RULE + CARD_GAP : 0)
    const slot = { page, left: at, afterAJump }
    at += cardWidth
    return slot
  })
  return { slots, width: at - rowLeft }
}

// Every statement, in the order given, laid out in one row. The caller decides the order (see
// stripOrder.ts); the layout only turns it into offsets.
export const stripLayout = (
  sources: BandSource[],
  labelFor: BandLabel,
  cardWidth: number,
): StripLayout => {
  const metrics = stripMetrics(cardWidth)
  let left = STRIP_INSET

  const bands = sources.map((source) => {
    const inset = BAND_BORDER + BAND_PADDING
    const row = pageRow(source.pages, left + inset, cardWidth)
    const band: StripBand = {
      statementId: source.statement.id,
      label: labelFor(source),
      left,
      width: inset * 2 + row.width,
      pages: row.slots,
    }
    left += band.width + BAND_GAP
    return band
  })

  return {
    bands,
    metrics,
    totalWidth: bands.length === 0 ? 0 : left - BAND_GAP + STRIP_INSET,
  }
}

const lastBand = (layout: StripLayout) =>
  layout.bands[layout.bands.length - 1] ?? null

// The last statement has to be able to abut the left edge too, so the file ends with a
// viewport's worth of room less whatever the last band itself takes up.
export const trailingSpace = (layout: StripLayout, viewportWidth: number) => {
  const last = lastBand(layout)
  if (!last) return 0
  return Math.max(viewportWidth - last.width - STRIP_INSET * 2, 0)
}

export const scrollableWidth = (layout: StripLayout, viewportWidth: number) =>
  layout.totalWidth + trailingSpace(layout, viewportWidth)

// Where the strip rests when this band abuts the left inset.
export const restingScrollLeft = (band: StripBand) => band.left - STRIP_INSET

// What the virtualizer needs per item: the band plus the space before the next one.
export const bandStride = (layout: StripLayout, index: number) => {
  const band = layout.bands[index]
  if (!band) return 0
  return band.width + BAND_GAP + (index === 0 ? STRIP_INSET : 0)
}

// The last item that starts at or before this offset. Items are laid out left to right, so a
// project with thousands of them still answers in a few steps.
export const indexStartingBefore = (
  bands: readonly { left: number }[],
  offset: number,
) => {
  let low = 0
  let high = bands.length - 1
  let found = 0
  while (low <= high) {
    const middle = Math.floor((low + high) / 2)
    if (bands[middle].left <= offset) {
      found = middle
      low = middle + 1
    } else {
      high = middle - 1
    }
  }
  return found
}

// A band start this close to the left edge is the one you meant, so the strip finishes the job.
const CLOSE_ENOUGH_IN_CARDS = 0.5

export interface RestingPlace {
  statementId: string
  // Where to ease the strip, or null to leave it exactly where the user let go.
  snapTo: number | null
}

const nearerStart = (edge: number, here: StripBand, next: StripBand | null) => {
  if (!next) return here
  return next.left - edge < edge - here.left ? next : here
}

const snapOnto = (band: StripBand): RestingPlace => ({
  statementId: band.statementId,
  snapTo: restingScrollLeft(band),
})

// Where the strip belongs once it stops, and which statement that makes the open one. A band
// that fits on screen is pulled until it abuts the left edge. A band too long to fit is left
// where the user put it, because snapping it back would bury every page past the first screen;
// it still snaps near either of its ends, so the edges of the band stay easy to land on.
export const restingPlace = (
  layout: StripLayout,
  scrollLeft: number,
  viewportWidth: number,
): RestingPlace | null => {
  const edge = scrollLeft + STRIP_INSET
  const index = indexStartingBefore(layout.bands, edge)
  const here = layout.bands[index] ?? null
  if (!here) return null

  const next = layout.bands[index + 1] ?? null
  const inside = edge >= here.left && edge < here.left + here.width
  const fitsOnScreen = viewportWidth <= 0 || here.width <= viewportWidth
  if (!inside || fitsOnScreen) return snapOnto(nearerStart(edge, here, next))

  const closeEnough = layout.metrics.cardWidth * CLOSE_ENOUGH_IN_CARDS
  if (edge - here.left <= closeEnough) return snapOnto(here)
  if (next && next.left - edge <= closeEnough) return snapOnto(next)
  return { statementId: here.statementId, snapTo: null }
}

// The pages a swept box touches, worked out from the layout. Asking the DOM instead would lose
// every page the virtualizer has not drawn, and the sweep would drop pages as it ran.
export const pagesUnderBox = (layout: StripLayout, box: HighlightBox) => {
  const { metrics } = layout
  const boxRight = box.left + box.width
  const boxBottom = box.top + box.height
  if (boxBottom < metrics.pageTop) return []
  if (box.top > metrics.pageTop + metrics.cardHeight) return []

  const pages: number[] = []
  for (
    let index = indexStartingBefore(layout.bands, box.left);
    index < layout.bands.length;
    index += 1
  ) {
    const band = layout.bands[index]
    if (band.left > boxRight) break
    for (const slot of band.pages) {
      const touches =
        slot.left < boxRight && slot.left + metrics.cardWidth > box.left
      if (touches) pages.push(slot.page.number)
    }
  }
  return pages
}
